import { SHEET_TITLES_CONSTANTS } from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/index';
import { MEAN_LABEL, MEDIAN_LABEL, PERCENTILE_25, PERCENTILE_75 } from 'pages/Valuations/util/constants';
import { GPC } from '../../../../api';
import { NOT_APPLICABLE } from '../../../../common/constants/general';
import { Calibration, CustomCalibrationPerformanceModel } from '../../../../common/types/valuation';
import { MAX, MIN } from '../guidelinePublicCompanies/constants';
import { GenerateNTMRevenueGrowthExpressionParams } from '../guidelinePublicCompanies/PerformanceMetrics/config/customParser/types';

const { PERFORMANCE_METRICS_SPREADSHEET_COLUMNS } = SHEET_TITLES_CONSTANTS;

export function addCapIqIdToPerfMetrics(
  performances: CustomCalibrationPerformanceModel[],
  existingGPCArray: GPC[]
): GPC[] {
  return performances
    .filter(performance => performance.name !== null)
    .map(performance => {
      const matchedGPC = existingGPCArray.find(gpc => gpc.company_name === performance.name);

      return {
        ltm_ebitda: performance.ltm_ebitda?.toString() ?? null,
        ltm_revenue: performance.ltm_revenue?.toString() ?? null,
        ltm_revenue_growth: performance.ltm_revenue_growth?.toString() ?? null,
        ntm_ebitda: performance.ntm_ebitda?.toString() ?? null,
        ntm_revenue: performance.ntm_revenue?.toString() ?? null,
        ntm_revenue_growth: performance.ntm_revenue_growth?.toString() ?? null,
        ltm_ebitda_margin: performance.ebitda_margin?.toString() ?? null,
        ltm_gross_margin: performance.gross_margin?.toString() ?? null,
        enterprise_value: performance.enterprise_value ?? null,
        name: performance.name ?? 'Unnamed',
        cap_iq_id: matchedGPC?.cap_iq_id ?? null,
        tangible_book_value: performance?.tangible_book_value ?? null,
        net_income_to_common_excl_extra_items: performance?.net_income_to_common_excl_extra_items ?? null,
        enabled: performance.enabled,
      };
    });
}

export const generateNTMRevenueGrowthExpression = (params: GenerateNTMRevenueGrowthExpressionParams): string => {
  const { alphabet, rowNumber } = params;

  // Column legend (-1 excluding Company Column)
  const columnLTMRevenueLegend = alphabet[PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE - 1];
  const columnNTMRevenueLegend = alphabet[PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE - 1];

  const columnLTMRevenueKey = columnLTMRevenueLegend + rowNumber;
  const columnNTMRevenueKey = columnNTMRevenueLegend + rowNumber;

  // NTM Revenue Growth formula expression
  const NTMRevenueGrowthExpression = `=IF(OR(${columnNTMRevenueKey} == 0, ${columnLTMRevenueKey} == 0), "${NOT_APPLICABLE}", (${columnNTMRevenueKey} / ${columnLTMRevenueKey}) - 1)`;

  return NTMRevenueGrowthExpression;
};

export const formulaCalibrationMean = (alias: string, field: string, performanceValues: string[]) =>
  `=IF(MEAN(FILTER_NON_POSITIVE([${performanceValues}])) == 0, "${NOT_APPLICABLE}", ${alias}_${field}_multiple_outputs / MEAN(FILTER_NON_POSITIVE([${performanceValues}])) - 1)`;

export const formulaCalibrationMedian = (alias: string, field: string, performanceValues: string[]) =>
  `=IF(MEDIAN(FILTER_NON_POSITIVE([${performanceValues}])) == 0, "${NOT_APPLICABLE}", ${alias}_${field}_multiple_outputs / MEDIAN(FILTER_NON_POSITIVE([${performanceValues}])) - 1)`;

export const formulaCalibrationMax = (alias: string, field: string, performanceValues: string[]) =>
  `=IF(MAX(FILTER_NON_POSITIVE([${performanceValues}])) == 0, "${NOT_APPLICABLE}", ${alias}_${field}_multiple_outputs / MAX(FILTER_NON_POSITIVE([${performanceValues}])) - 1)`;

export const formulaCalibrationMin = (alias: string, field: string, performanceValues: string[]) =>
  `=IF(MIN(FILTER_NON_POSITIVE([${performanceValues}])) == 0, "${NOT_APPLICABLE}", ${alias}_${field}_multiple_outputs / MIN(FILTER_NON_POSITIVE([${performanceValues}])) - 1)`;

export const formulaCalibrationPercentile = (
  alias: string,
  field: string,
  performanceValues: string[],
  midValue: number
) =>
  `=IF(PERCENTILE(FILTER_NON_POSITIVE([${performanceValues}]), ${midValue}) == 0, "${NOT_APPLICABLE}", ${alias}_${field}_multiple_outputs / PERCENTILE(FILTER_NON_POSITIVE([${performanceValues}]), ${midValue}) - 1)`;

export const getCorrespondingFormula = (value: string, alias: string, type: string, performanceValues: string[]) => {
  switch (value) {
    case MAX.toLowerCase():
      return formulaCalibrationMax(alias, type, performanceValues);
    case MIN.toLowerCase():
      return formulaCalibrationMin(alias, type, performanceValues);
    // The labels below are a different import than the labels actually used in the list. This may result in difficult bug to find
    case MEAN_LABEL.toLowerCase():
      return formulaCalibrationMean(alias, type, performanceValues);
    case MEDIAN_LABEL.toLowerCase():
      return formulaCalibrationMedian(alias, type, performanceValues);
    case PERCENTILE_75.toLowerCase():
      return formulaCalibrationPercentile(alias, type, performanceValues, 75);
    case PERCENTILE_25.toLowerCase():
      return formulaCalibrationPercentile(alias, type, performanceValues, 25);
    default:
      return '';
  }
};

export const getSelectorValues = (calibration: Calibration) => ({
  revenue_premium_formula_selector: (calibration.revenue_premium_formula_selector ?? '') || MEDIAN_LABEL,
  ebitda_premium_formula_selector: (calibration.ebitda_premium_formula_selector ?? '') || MEDIAN_LABEL,
});
